/**
 * App Usage — the account's Operation Counter dashboard, embedded.
 *
 * The dashboard itself is a page on the BeelabX portal, not something this app
 * renders: we resolve the account's unguessable usage URL and frame it. So this
 * component owns three states and nothing else — waiting for the link, no link
 * to show, and the frame.
 *
 * Rendered INSIDE the app shell (navbar + full-width content column) like every other
 * view here, so it carries no page chrome of its own — see App.render().
 *
 * Mirrors apr-frontend's SubscriptionView; the differences are the token-based
 * styling this app uses and the accountId guard below.
 */
import React, { useEffect, useState } from 'react';
import { Alert, NavigationChevronLeft } from '@vibe/icons';
import LayoutSkeleton from './LayoutSkeleton';
import { getUsagePageUrl } from '../service/subscription';

/**
 * The frame fills the viewport minus the chrome above it (navbar ≈ 61px, the
 * column's py-6 = 48px, back link ≈ 36px, heading block ≈ 92px), so the page
 * itself never gains a second scrollbar alongside the dashboard's own. The
 * min-height keeps it usable if monday gives the app a short iframe.
 *
 * LayoutSkeleton's `usage` variant repeats this expression — change both.
 */
const FRAME_HEIGHT = 'calc(100vh - 237px)';

export default function AppUsageView({ accountId, onBack }) {
  const [usageUrl, setUsageUrl] = useState(null);
  // Starts true: with no accountId yet the monday context simply hasn't arrived,
  // which is a wait, not a failure.
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setIsLoading(true);
    getUsagePageUrl(accountId)
      .then((url) => {
        if (!cancelled) setUsageUrl(url);
      })
      .catch((error) => {
        // Leaves usageUrl null, so the support notice below is shown.
        console.error('Failed to resolve the usage link:', error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  // Fallback: never leave the skeleton up forever if the context never arrives
  // or the request never settles. The notice is a worse answer than the
  // dashboard but a much better one than a pulse that runs all day.
  useEffect(() => {
    if (!isLoading) return;
    const id = setTimeout(() => setIsLoading(false), 10000);
    return () => clearTimeout(id);
  }, [isLoading]);

  if (isLoading) return <LayoutSkeleton variant="usage" label="Loading app usage…" />;

  return (
    <>
      {/* Same rhythm as the template list and the editor: the way out first, then
          heading + subheading at page level, no card, then the content. The link
          is a copy of TemplateEditor's — same label, same placement — so "leave
          this view" looks and reads identically wherever the user meets it. */}
      <div className="mb-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center mb-4 text-sm font-medium text-secondary transition-colors hover:text-primary"
        >
          <NavigationChevronLeft className="w-4 h-4 mr-2" />
          Back to Templates
        </button>
        <h1 className="text-xl font-bold text-primary">App Usage</h1>
        <p className="mt-2 text-sm text-secondary">
          Monitor your Operation Counter usage for this account.
        </p>
      </div>

      {usageUrl ? (
        <div className="overflow-hidden bg-surface border border-subtle rounded-lg shadow-sm">
          <iframe
            title="Operation Counter"
            src={usageUrl}
            className="block w-full border-0"
            style={{ height: FRAME_HEIGHT, minHeight: 420 }}
          />
        </div>
      ) : (
        <div className="flex items-start gap-3 p-4 border border-warning bg-warning-tint rounded-lg">
          <Alert className="flex-none w-5 h-5 mt-0.5 text-warning-ink" />
          <p className="text-sm leading-relaxed text-warning-ink">
            We cannot find your Operation Counter information. Please contact our
            support center for further assistance at{' '}
            <a
              href="mailto:support@beelabxapps.com"
              className="font-medium underline text-warning-ink"
            >
              support@beelabxapps.com
            </a>
            .
          </p>
        </div>
      )}
    </>
  );
}
